import {
    AtomContainer,
    IAtomModel,
    IBondModel,
} from "@alchemiesolns/atomcontainer";
import { getFormat } from "./utils/formatContext";
import { v3 } from "@alchemiesolns/maths";
import { ArrowConfigDef, ArrowType } from "@alchemiesolns/arrow-def";
import { ElectronData } from "./svgElectrons";
import { Point } from "@alchemiesolns/kaff-arrow";
import { CurveProps } from "@alchemiesolns/kaff-arrow/src/types";
import { svgPaperAtoms } from "./svgPaperAtoms";

const camDir = [0, 0, 1];
export const svgArrows = {
    calcTmpArrowAttsFromLp: (
        atom: IAtomModel,
        dst: number[],
        electrons: ElectronData[],
        container: AtomContainer,
        visibleAtoms: IAtomModel[]
    ) => {
        const props: CurveProps = {
            source: getLonePairPoint(
                atom,
                electrons.filter((e) => e.atomId === atom.id),
                container,
                visibleAtoms
            ),
            destination: getPointPoint(dst, 0),
            isDestAdjacent: false,
            cameraDir: camDir,
            type: "curve",
            format: getFormat(),
        };
        return [props];
    },
    calcArrowAtts: (
        arrowDefs: ArrowConfigDef[],
        container: AtomContainer,
        electrons: ElectronData[],
        visibleAtoms: IAtomModel[]
    ): CurveProps[] => {
        const arrows = arrowDefs.map((a) => {
            switch (a.type) {
                case ArrowType.LPtoAtom:
                    return {
                        source: getLonePairPoint(
                            container.atoms[a.src],
                            electrons.filter(
                                (e) => e.atomId === container.atoms[a.src].id
                            ),
                            container,
                            visibleAtoms
                        ),
                        destination: getAtomPoint(
                            container.atoms[a.dst],
                            getAtomArrowOffset(
                                container.atoms[a.dst],
                                container,
                                visibleAtoms
                            )
                        ),
                        isDestAdjacent: isDestAdjacent(
                            [container.atoms[a.src].id ?? ""],
                            [container.atoms[a.dst].id ?? ""]
                        ),
                        cameraDir: camDir,
                        type: "curve",
                        format: getFormat(),
                    };
                case ArrowType.LPtoBond:
                    return {
                        source: getLonePairPoint(
                            container.atoms[a.src],
                            electrons.filter(
                                (e) => e.atomId === container.atoms[a.src].id
                            ),
                            container,
                            visibleAtoms
                        ),
                        destination: getBondPoint(container.bonds[a.dst]),
                        isDestAdjacent: isDestAdjacent(
                            [container.atoms[a.src].id ?? ""],
                            [
                                container.bonds[a.dst].a1.id ?? "",
                                container.bonds[a.dst].a2.id ?? "",
                            ]
                        ),
                        cameraDir: camDir,
                        type: "curve",
                        format: getFormat(),
                    };
                case ArrowType.BondToAtom:
                    return {
                        source: getBondPoint(container.bonds[a.src], container.atoms[a.p ?? -1]),
                        destination: getAtomPoint(
                            container.atoms[a.dst],
                            getAtomArrowOffset(
                                container.atoms[a.dst],
                                container,
                                visibleAtoms
                            )
                        ),
                        isDestAdjacent: isDestAdjacent(
                            [
                                container.bonds[a.src].a1.id ?? "",
                                container.bonds[a.src].a2.id ?? "",
                            ],
                            [container.atoms[a.dst].id ?? ""]
                        ),
                        cameraDir: camDir,
                        type: "curve",
                        format: getFormat(),
                    };
                case ArrowType.BondToBond:
                    return {
                        source: getBondPoint(container.bonds[a.src], container.atoms[a.p ?? -1]),
                        destination: getBondPoint(container.bonds[a.dst]),
                        isDestAdjacent: isDestAdjacent(
                            [
                                container.bonds[a.src].a1.id ?? "",
                                container.bonds[a.src].a2.id ?? "",
                            ],
                            [
                                container.bonds[a.dst].a1.id ?? "",
                                container.bonds[a.dst].a2.id ?? "",
                            ]
                        ),
                        cameraDir: camDir,
                        type: "curve",
                        format: getFormat(),
                    };
                case ArrowType.RtoAtom:
                    return {
                        source: getRadicalPoint(
                            container.atoms[a.src],
                            electrons.filter(
                                (e) => e.atomId === container.atoms[a.src].id
                            ),
                            container,
                            visibleAtoms
                        ),
                        destination: getAtomPoint(
                            container.atoms[a.dst],
                            getAtomArrowOffset(
                                container.atoms[a.dst],
                                container,
                                visibleAtoms
                            )
                        ),
                        isDestAdjacent: isDestAdjacent(
                            [container.atoms[a.src].id ?? ""],
                            [container.atoms[a.dst].id ?? ""]
                        ),
                        cameraDir: camDir,
                        type: "fish",
                        overrideDest: getOverrideDest(
                            [container.atoms[a.src].id!],
                            arrowDefs,
                            container,
                            electrons,
                            visibleAtoms
                        ),
                        format: getFormat(),
                    };
                case ArrowType.RtoBond:
                    return {
                        source: getRadicalPoint(
                            container.atoms[a.src],
                            electrons.filter(
                                (e) => e.atomId === container.atoms[a.src].id
                            ),
                            container,
                            visibleAtoms
                        ),
                        destination: getBondPoint(container.bonds[a.dst]),
                        isDestAdjacent: isDestAdjacent(
                            [container.atoms[a.src].id ?? ""],
                            [
                                container.bonds[a.dst].a1.id ?? "",
                                container.bonds[a.dst].a2.id ?? "",
                            ]
                        ),
                        cameraDir: camDir,
                        type: "fish",
                        overrideDest: getOverrideDest(
                            [container.atoms[a.src].id!],
                            arrowDefs,
                            container,
                            electrons,
                            visibleAtoms
                        ),
                        format: getFormat(),
                    };
                case ArrowType.CBondToAtom:
                    console.log("CBondToAtom", a.src, a.dst);
                    return {
                        source: getBondPoint(container.bonds[a.src], container.atoms[a.p ?? -1]),
                        destination: getAtomPoint(
                            container.atoms[a.dst],
                            getAtomArrowOffset(
                                container.atoms[a.dst],
                                container,
                                visibleAtoms
                            )
                        ),
                        isDestAdjacent: isDestAdjacent(
                            [
                                container.bonds[a.src].a1.id ?? "",
                                container.bonds[a.src].a2.id ?? "",
                            ],
                            [container.atoms[a.dst].id ?? ""]
                        ),
                        cameraDir: camDir,
                        type: "fish",
                        overrideDest: getOverrideDest(
                            [
                                container.bonds[a.src].a1.id!,
                                container.bonds[a.src].a2.id!,
                            ],
                            arrowDefs,
                            container,
                            electrons,
                            visibleAtoms
                        ),
                        format: getFormat(),
                    };
                case ArrowType.CBondToBond:
                    return {
                        source: getBondPoint(container.bonds[a.src], container.atoms[a.p ?? -1]),
                        destination: getBondPoint(container.bonds[a.dst]),
                        isDestAdjacent: isDestAdjacent(
                            [
                                container.bonds[a.src].a1.id ?? "",
                                container.bonds[a.src].a2.id ?? "",
                            ],
                            [
                                container.bonds[a.dst].a1.id ?? "",
                                container.bonds[a.dst].a2.id ?? "",
                            ]
                        ),
                        cameraDir: camDir,
                        type: "fish",
                        overrideDest: getOverrideDest(
                            [
                                container.bonds[a.src].a1.id!,
                                container.bonds[a.src].a2.id!,
                            ],
                            arrowDefs,
                            container,
                            electrons,
                            visibleAtoms
                        ),
                        format: getFormat(),
                    };
                default:
                    return {
                        source: {
                            position: [0, 0, 0],
                            type: "",
                            direction: [[0, 0, 0]],
                            offset: 0,
                        },
                        destination: {
                            position: [0, 0, 0],
                            type: "",
                            direction: [[0, 0, 0]],
                            offset: 0,
                        },
                        isDestAdjacent: false,
                        cameraDir: camDir,
                        type: "curve",
                        format: getFormat(),
                    };
            }
        });
        return arrows;
    },
};

const getLonePairPoint = (
    atom: IAtomModel,
    electronData: ElectronData[],
    container: AtomContainer,
    visibleAtoms: IAtomModel[]
): Point => {
    // IMPORTANT: kaff-arrow expects UNIT VECTORS in direction field, not offset vectors
    // electronData.dir contains offset vectors (unit vector × offset), so we need to normalize them
    const directions = electronData
        .filter((e) => e.type === "lonepair")
        .map((e) => {
            // e.dir is an offset vector, normalize it to get unit vector
            const length = Math.sqrt(e.dir[0] * e.dir[0] + e.dir[1] * e.dir[1] + (e.dir[2] ?? 0) * (e.dir[2] ?? 0));
            return length > 0 ? [e.dir[0] / length, e.dir[1] / length, (e.dir[2] ?? 0) / length] : e.dir;
        });
    return {
        position: atom.pos
            ? [atom.pos.x ?? 0, atom.pos.y ?? 0, atom.pos.z ?? 0]
            : [0, 0, 0],
        type: "lonepair",
        direction: directions,
        offset: svgPaperAtoms.isPaperCarbon(atom, container, visibleAtoms)
            ? 0.7
            : 0.5,
    };
};
const getRadicalPoint = (
    atom: IAtomModel,
    electronData: ElectronData[],
    container: AtomContainer,
    visibleAtoms: IAtomModel[]
): Point => {
    // IMPORTANT: kaff-arrow expects UNIT VECTORS in direction field, not offset vectors
    const radicalDir = electronData[electronData.length - 1].dir;
    const length = Math.sqrt(radicalDir[0] * radicalDir[0] + radicalDir[1] * radicalDir[1] + (radicalDir[2] ?? 0) * (radicalDir[2] ?? 0));
    const unitDir = length > 0 ? [radicalDir[0] / length, radicalDir[1] / length, (radicalDir[2] ?? 0) / length] : radicalDir;

    return {
        position: atom.pos
            ? [atom.pos.x ?? 0, atom.pos.y ?? 0, atom.pos.z ?? 0]
            : [0, 0, 0],
        type: "radical",
        direction: [unitDir],
        offset: svgPaperAtoms.isPaperCarbon(atom, container, visibleAtoms)
            ? 1
            : 0.8,
    };
};
export const getAtomArrowOffset = (
    a: IAtomModel,
    container: AtomContainer,
    visibleAtoms: IAtomModel[]
) => {
    // Small carbon exception
    const isPaperCarbon =
        a.symbol === "C"
            ? svgPaperAtoms.isPaperCarbon(a, container, visibleAtoms)
            : false;
    if (isPaperCarbon) {
        return 1.2;
    }
    return getFormat().atom.radius + 1.2;
};
const getAtomPoint = (atom: IAtomModel, offset: number): Point => {
    return {
        position: atom.pos
            ? [atom.pos.x ?? 0, atom.pos.y ?? 0, atom.pos.z ?? 0]
            : [0, 0, 0],
        type: "particle",
        direction: [[0, 0, 0]],
        offset: offset,
    };
};

const getPointPoint = (pt: number[], offset: number): Point => {
    return {
        position: [...pt],
        type: "particle",
        direction: [[0, 0, 0]],
        offset: offset,
    };
};

const getBondPoint = (bond: IBondModel, pivotAtom?: IAtomModel): Point => {
    const a1Pos = bond.a1.pos
        ? [bond.a1.pos.x ?? 0, bond.a1.pos.y ?? 0, bond.a1.pos.z ?? 0]
        : [0, 0, 0];
    const a2Pos = bond.a2.pos
        ? [bond.a2.pos.x ?? 0, bond.a2.pos.y ?? 0, bond.a2.pos.z ?? 0]
        : [0, 0, 0];

    // Calculate bond midpoint
    let bondMid = v3.midpoint(a1Pos, a2Pos);

    // If pivot atom is provided, return midpoint of the segment from bondMid toward the non-pivot atom
    let pos: number[];
    if (pivotAtom && pivotAtom.id) {
        // Determine which atom is the pivot (hinge) and which is the other end
        const isPivotA1 = pivotAtom.id === bond.a1.id;
        const nonPivotAtomPos = isPivotA1 ? a2Pos : a1Pos;

        // Return midpoint of segment from bond center toward the non-pivot atom
        pos = v3.midpoint(bondMid, nonPivotAtomPos);
    } else {
        // No pivot atom - use original logic for bond center
        pos = bondMid;
        if (bond.a1.symbol === "C" && bond.a2.symbol !== "C") {
            let end = v3.midpoint(pos, a1Pos);
            pos = v3.midpoint(pos, end);
        } else if (bond.a2.symbol === "C" && bond.a1.symbol !== "C") {
            let end = v3.midpoint(pos, a2Pos);
            pos = v3.midpoint(pos, end);
        }
    }

    return {
        position: pos,
        type: "bond",
        direction: [v3.normalize(v3.subVectors(a1Pos, a2Pos))],
        offset: 0.3,
    };
};

export const isDestAdjacent = (sourcePs: string[], destPs: string[]) => {
    return destPs.some((p) => sourcePs.includes(p));
};

export const getOverrideDest = (
    startIds: string[],
    arrows: ArrowConfigDef[],
    container: AtomContainer,
    electrons: ElectronData[],
    visibleAtoms: IAtomModel[]
) => {
    arrows = arrows.filter(
        (a) =>
            a.type === ArrowType.CBondToAtom ||
            a.type === ArrowType.RtoAtom ||
            a.type === ArrowType.RtoBond
    );
    for (let i = 0; i < arrows.length; i++) {
        const a = arrows[i];
        const bondStart = a.type === ArrowType.CBondToAtom;
        const radicalStart =
            a.type === ArrowType.RtoAtom || a.type === ArrowType.RtoBond;
        const bondEnd = a.type === ArrowType.RtoBond;
        const atomEnd =
            a.type === ArrowType.RtoAtom || a.type === ArrowType.CBondToAtom;
        let end: string[] = [];
        if (bondEnd) {
            end = [
                container.bonds[a.dst].a1.id!,
                container.bonds[a.dst].a2.id!,
            ];
        } else if (atomEnd) {
            end = [container.atoms[a.dst].id!];
        }
        const found = end.some((id) => startIds.includes(id));
        if (found) {
            if (bondStart) {
                console.log("bondStart", a.src, a.dst);
                if (
                    !(
                        startIds.includes(container.bonds[a.src].a1.id!) &&
                        startIds.includes(container.bonds[a.src].a2.id!)
                    )
                )
                    return getBondPoint(container.bonds[a.src]);
            } else if (radicalStart) {
                return getRadicalPoint(
                    container.atoms[a.src],
                    electrons.filter(
                        (e) => e.atomId === container.atoms[a.src].id
                    ),
                    container,
                    visibleAtoms
                );
            }
        }
    }
};
